import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { setSearchPendingStatus, setSearchParamAction } from 'store/actions/search';
import { FONTS } from 'styles/theme/fonts';
import { COLORS } from 'styles/theme/colors';

export const SearchBar = ({ placeholder, onSubmit }) => {
  const currentView = useSelector((state) => state.currentView);
  const currentOpenTab = useSelector((state) => state.openOrders.currentTab);
  const currentCompletedTab = useSelector((state) => state.completedOrders.currentTab);
  const [searchParam, setSearchParam] = useState('');
  const [pending, setPending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const dispatch = useDispatch();

  const handleFocus = () => {
    setIsTyping(true);
  };

  const handleBlur = () => {
    setIsTyping(false);
  };

  const handleClearSearch = () => {
    setSearchParam('');
    dispatch(setSearchParamAction(''));
    onSubmit('');
    dispatch(setSearchPendingStatus(false));
    setPending(false);
  };

  const handleChange = (e) => {
    if (!pending) {
      dispatch(setSearchPendingStatus(true));
      setPending(true);
    }
    setSearchParam(e.target.value);
    dispatch(setSearchParamAction(e.target.value));
    onSubmit(e.target.value);
  };

  useEffect(() => {
    if (searchParam.length > 0) {
      setSearchParam('');
      dispatch(setSearchParamAction(''));
      onSubmit('');
    }
  }, [currentView, currentOpenTab, currentCompletedTab]);

  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.keyCode === 13 || e.key === 'enter') {
        onSubmit(searchParam);
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [onSubmit, searchParam, searchParam.length, dispatch]);

  return (
    <SearchBar.Container hasTabs={currentView !== 'Active Listings'}>
      {searchParam.length > 0 && (
        <SearchBar.ClearSearch isTyping={isTyping} onClick={handleClearSearch}>
          <SearchBar.X
            alt="close"
            src={isTyping ? '/assets/svg/icon-close-white.svg' : '/assets/svg/icon-close-gray.svg'}
          />
        </SearchBar.ClearSearch>
      )}
      <SearchBar.StyledInput
        placeholder={placeholder}
        onChange={(e) => handleChange(e)}
        value={searchParam}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </SearchBar.Container>
  );
};

SearchBar.Container = styled.div`
  margin-top: 24px;
  margin-bottom: ${(props) => (props.hasTabs ? '86px' : '32px')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1px 1px;
  &:first-child {
    margin-top: 32px;
  }

  &:hover {
    div {
      border: 1px solid ${COLORS.primarySearchBarClearButtonBorder};

      img {
        content: url(/assets/svg/icon-close-hover.svg);
      }
    }
  }
`;

SearchBar.StyledInput = styled.input`
  border: 1.4px solid ${COLORS.primarySearchBarBorder};
  background: url(/assets/svg/icon-search.svg) no-repeat scroll 16px 16px;
  background-color: ${COLORS.primaryBackground};
  height: 48px;
  width: 100%;
  font-size: 14px;
  color: ${COLORS.primarySearchBarText};
  font-family: ${FONTS.default};
  line-height: 20px;
  padding: 10px 0 10px 50px;

  &:hover {
    outline: none;
    border: 1px solid ${COLORS.secondayBorder};
    background-color: ${COLORS.primaryBackground};
    color: ${COLORS.secondaryText};
  }

  &:focus {
    color: ${COLORS.primaryText};
    outline: none;
    background-color: ${COLORS.primaryBackground};
  }
`;

SearchBar.ClearSearch = styled.div`
  position: absolute;
  right: 76px;
  width: 18px;
  height: 18px;
  margin-top: 16px;
  border: 1px solid
    ${(props) => (props.isTyping ? COLORS.secondarySearchBarBorder : COLORS.primarySearchBarBorder)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => (props.isTyping ? COLORS.primaryText : COLORS.primarySearchBarText)};
`;

SearchBar.X = styled.img`
  width: 6px;
  height: 6px;
  color: ${COLORS.primaryText};
`;

SearchBar.propTypes = {
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};

export default SearchBar;
